import { type InputHTMLAttributes, type RefObject, useEffect, useRef, useState } from 'react';

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'onBlur'> {
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    value: string;
    className?: string;
    maxWidth?: string;
    parentRef?: RefObject<HTMLElement | null>;
    currentLocale?: string;
}

export const AutoResizeInput = ({
    value,
    onChange,
    onBlur,
    className,
    maxWidth,
    parentRef,
    currentLocale,
    ...props
}: Props) => {
    const hiddenRef = useRef<HTMLSpanElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [inputValue, setInputValue] = useState<string>(value);

    useEffect(() => {
        setInputValue(value);
    }, [value]);

    useEffect(() => {
        if (parentRef?.current) {
            const rect = parentRef.current.getBoundingClientRect();
            parentRef.current.style.maxWidth = `${rect.width}px`;
        }
    }, [parentRef]);

    useEffect(() => {
        if (hiddenRef.current && inputRef.current) {
            hiddenRef.current.textContent = inputValue || props.placeholder || '';

            const inputStyles = window.getComputedStyle(inputRef.current);
            const paddingLeft = Number.parseFloat(inputStyles.paddingLeft) || 0;
            const paddingRight = Number.parseFloat(inputStyles.paddingRight) || 0;
            const paddingHorizontal = paddingLeft + paddingRight;

            const totalPadding = paddingHorizontal + 2;
            const contentWidth = hiddenRef.current.offsetWidth;

            if (currentLocale) inputRef.current.style.width = `${contentWidth + totalPadding}px`;
        }
    }, [inputValue, props.placeholder, currentLocale]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        if (onChange) onChange(e);
    };

    return (
        <div className={`relative ${maxWidth ? maxWidth : 'max-w-[calc(100%-80px)]'} overflow-hidden`}>
            <span
                ref={hiddenRef}
                className="absolute opacity-0 whitespace-pre font-inherit pointer-events-none text-red"
                style={{
                    visibility: 'hidden',
                    height: 'auto',
                    width: 'auto',
                    fontSize: 'inherit',
                    fontFamily: 'inherit',
                    fontWeight: 'inherit',
                }}
            />
            <input
                ref={inputRef}
                value={inputValue}
                onChange={handleChange}
                onBlur={(e) => {
                    if (onBlur) onBlur(e);
                    if (hiddenRef.current && hiddenRef.current.textContent === '') {
                        hiddenRef.current.textContent = e.currentTarget.value;
                        if (parentRef?.current) {
                            const rect = parentRef.current.getBoundingClientRect();
                            parentRef.current.style.maxWidth = `${rect.width}px`;
                        }
                        if (hiddenRef.current && inputRef.current) {
                            const inputStyles = window.getComputedStyle(inputRef.current);
                            const paddingLeft = Number.parseFloat(inputStyles.paddingLeft) || 0;
                            const paddingRight = Number.parseFloat(inputStyles.paddingRight) || 0;
                            const paddingHorizontal = paddingLeft + paddingRight;

                            const totalPadding = paddingHorizontal + 2;
                            const contentWidth = hiddenRef.current.offsetWidth;

                            inputRef.current.style.width = `${contentWidth + totalPadding}px`;
                        }
                    }
                }}
                className={`${className} max-w-[100%]`}
                style={{ minWidth: '50px' }}
                {...props}
            />
        </div>
    );
};
